import type { BestPrices } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { parseNorceDate } from "../parseNorceDate";

/**
 * The lowest price this product has been sold at over the window Norce reports.
 *
 * `ListHistoricalBestPrices` answers with the price *changes* of the last thirty
 * days, plus the one change that leads into the period — so every row was the
 * live price for some stretch of the window, and the lowest of them is the
 * figure the "lowest price in the last 30 days" note names.
 *
 * Deliberately no date filter. The leading row sits outside the window by its
 * `Updated` date while being the price in force when the window opened: a
 * product put at 200 in June and raised to 499 in September really did cost 200
 * for the first days of the window, and filtering by date would drop exactly
 * that row and report 499. Norce has already scoped the response; filtering it
 * again can only lose the boundary.
 *
 * It used to take the first row at or below the current price, which depended on
 * the order the API happened to return and answered with the current price
 * itself whenever that row came first — naming today's price as the lowest of
 * the last thirty days, which is both wrong and useless.
 */
export const getLowestPriceIncVat = (
	selectedPrice: number,
	pricesArray: BestPrices[]
) => {
	const prices = (pricesArray ?? [])
		.map((item) => item?.PriceIncVat)
		.filter((price): price is number => typeof price === "number");

	if (!prices.length) return null;

	const lowest = Math.min(...prices);

	// Nothing to say when the shopper is already paying the lowest it has been:
	// the note exists to show that a price was once lower than it is now.
	return lowest <= selectedPrice ? lowest : null;
};

/**
 * The Omnibus reading, for a product on a reduction: the lowest price of the
 * window *before* the reduction. The reduction is the trailing run of rows from
 * the list the product is sold from now (`currentPriceListId`); rows before it
 * are what the shopper could have paid. `null` when the window holds nothing
 * older than the reduction, and whenever the timeline cannot be read: a date
 * that does not parse leaves the order unknown, and a history with no row from
 * the current list leaves the reduction's start unknown — guessing there would
 * name today's price as the price before it.
 *
 * Norce keeps a 30-day best price in `PricePrevious` itself, but the Storm
 * product API the storefront reads does not return that field (checked Oct
 * 2026), so the figure is derived here from the price history instead.
 */
export const getLowestPriceBeforeReductionIncVat = (
	currentPriceListId: number,
	pricesArray: BestPrices[]
) => {
	const dated = (pricesArray ?? []).map((item) => ({
		item,
		time: parseNorceDate(item?.Updated)
	}));
	if (dated.some(({ time }) => Number.isNaN(time))) return null;

	const timeline = dated
		.sort((a, b) => a.time - b.time)
		.map(({ item }) => item);

	let reductionStart = timeline.length;
	while (
		reductionStart > 0 &&
		timeline[reductionStart - 1]?.PriceListId === currentPriceListId
	) {
		reductionStart -= 1;
	}
	if (reductionStart === timeline.length) return null;

	const prices = timeline
		.slice(0, reductionStart)
		.map((item) => item?.PriceIncVat)
		.filter((price): price is number => typeof price === "number");

	return prices.length ? Math.min(...prices) : null;
};

export interface LowestPriceNote {
	value: number;
	/** Which figure it is, so the note can say so. */
	kind: "beforeReduction" | "last30Days";
}

/**
 * The PDP's lowest-price note.
 * - Without `beforeReduction`: the 30-day low, only when there is one.
 * - With `beforeReduction` (Omnibus): on a reduction, the lowest price before
 *   it; otherwise nothing, unless `always`.
 * - With `always` the note never goes missing (sportkost, OR-19): when there
 *   is no before-reduction figure — no reduction, or one older than the 30-day
 *   history — it names the 30-day low, and today's price when the price has
 *   not changed or is the lowest it has been.
 */
export const resolveLowestPriceNote = ({
	price,
	priceListId,
	isReduced,
	beforeReduction,
	always,
	history
}: {
	price: number;
	priceListId: number;
	isReduced: boolean;
	beforeReduction: boolean;
	always: boolean;
	history: BestPrices[];
}): LowestPriceNote | null => {
	if (beforeReduction && isReduced) {
		const before = getLowestPriceBeforeReductionIncVat(priceListId, history);
		if (before != null) return { value: before, kind: "beforeReduction" };
	}
	if (beforeReduction && !always) return null;
	// Norce sends the price in force when the window opened even for a price
	// that never changed, so [] means the history is missing: next to a
	// reduction, today's price would be passed off as the low of an unseen window.
	if (isReduced && !history.length) return null;

	const last30Days = getLowestPriceIncVat(price, history);
	if (last30Days != null) return { value: last30Days, kind: "last30Days" };
	return always && price > 0 ? { value: price, kind: "last30Days" } : null;
};
